import React, { useState, useEffect, useCallback } from "react";
import "./AdminPortal.css";

const API_BASE = "https://api-2026.fikacreatives.co";
const LOGO =
  "https://vdbpbpjwtbcyhnnwynox.supabase.co/storage/v1/object/sign/landing%20page/blue%20fika%20logo.png?token=eyJraWQiOiJzdG9yYWdlLXVybC1zaWduaW5nLWtleV9kZGI0MGU3NC04Nzk2LTRhZmItOTViNy0wNDQ5YzAyNWMzZjciLCJhbGciOiJIUzI1NiJ9.eyJ1cmwiOiJsYW5kaW5nIHBhZ2UvYmx1ZSBmaWthIGxvZ28ucG5nIiwiaWF0IjoxNzc2ODE2ODMwLCJleHAiOjE4MDgzNTI4MzB9.H5RlV1zB9mj_TES7gtHRYO0bphEZOcF30yR96p5s6SY";

const ELIGIBLE_STATUSES = new Set(["APPLICATION_COMPLETE", "PAYMENT_COMPLETE", "PAYMENT_STARTED"]);

function linkedInUrl(handle) {
  if (!handle) return "";
  if (/^https?:\/\//i.test(handle)) return handle;
  return `https://www.linkedin.com/in/${handle.replace(/^\/+/, "")}`;
}

function instagramUrl(handle) {
  if (!handle) return "";
  if (/^https?:\/\//i.test(handle)) return handle;
  return `https://www.instagram.com/${handle.replace(/^@/, "").replace(/^\/+/, "")}`;
}

function Toast({ message, isError, onDone }) {
  useEffect(() => {
    const t = setTimeout(onDone, 3000);
    return () => clearTimeout(t);
  }, [onDone]);

  return (
    <div className={`admin-toast${isError ? " admin-toast-error" : ""}`}>
      {message}
    </div>
  );
}

function Check() {
  return <span className="admin-check">✓</span>;
}

function X() {
  return <span className="admin-x">✗</span>;
}

export default function AdminPortal() {
  const [authed, setAuthed] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [authError, setAuthError] = useState("");
  const [authLoading, setAuthLoading] = useState(false);
  const [token, setToken] = useState("");

  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [accepting, setAccepting] = useState(new Set());
  const [marking, setMarking] = useState(new Set());
  const [sendingAvailCheck, setSendingAvailCheck] = useState(false);
  const [sendingAvailCheckTest, setSendingAvailCheckTest] = useState(false);
  const [sendingNycAvailPoll, setSendingNycAvailPoll] = useState(false);
  const [sendingNycAvailPollTest, setSendingNycAvailPollTest] = useState(false);
  const [sendingNycCycleFollowup, setSendingNycCycleFollowup] = useState(false);
  const [sendingNycCycleFollowupTest, setSendingNycCycleFollowupTest] = useState(false);
  const [sendingLaCycleFollowup, setSendingLaCycleFollowup] = useState(false);
  const [sendingLaCycleFollowupTest, setSendingLaCycleFollowupTest] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [groupMessage, setGroupMessage] = useState("");
  const [sendingGroupText, setSendingGroupText] = useState(false);
  const [toast, setToast] = useState(null);
  const [filterCity, setFilterCity] = useState("all");
  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState("desc");
  const [expandedProject, setExpandedProject] = useState(new Set());
  const [expandedSpecialties, setExpandedSpecialties] = useState(new Set());
  const [selectedPerson, setSelectedPerson] = useState(null);
  const [cycleAvailData, setCycleAvailData] = useState(null);
  const [loadingCycleAvail, setLoadingCycleAvail] = useState(false);
  const [shootSending, setShootSending] = useState(null);
  const [messageDrafts, setMessageDrafts] = useState({
    nycAvailPoll: "",
    nycReminderPoll: "",
    laAvailPoll: "",
    laReminderPoll: "",
  });
  const [loadingPreviews, setLoadingPreviews] = useState(false);
  const [openCycles, setOpenCycles] = useState([]);

  function toggleExpand(_set, setFn, id) {
    setFn((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  const showToast = (message, isError = false) => {
    setToast({ message, isError, key: Date.now() });
  };

  const fetchCycleAvailability = useCallback(async (tok) => {
    setLoadingCycleAvail(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/match-cycles`, {
        headers: { Authorization: `Basic ${tok}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to load cycle data");
      setCycleAvailData(data.grouped || {});
    } catch (e) {
      showToast(e.message, true);
    } finally {
      setLoadingCycleAvail(false);
    }
  }, []);

  const fetchPeople = useCallback(async (tok) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/people`, {
        headers: { Authorization: `Basic ${tok}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to load");
      setPeople(data.people ?? []);
    } catch (e) {
      showToast(e.message, true);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPreviews = useCallback(async (tok) => {
    setLoadingPreviews(true);
    try {
      const authHeader = { "Content-Type": "application/json", Authorization: `Basic ${tok}` };
      const [nycAvailPoll, laAvailPoll, nycReminderPoll, laReminderPoll] = await Promise.all([
        fetch(`${API_BASE}/api/admin/send-nyc-availability-poll`, {
          method: "POST",
          headers: authHeader,
          body: JSON.stringify({ preview: true }),
        }).then((r) => r.json()),
        fetch(`${API_BASE}/api/admin/send-availability-check`, {
          method: "POST",
          headers: authHeader,
          body: JSON.stringify({ preview: true }),
        }).then((r) => r.json()),
        fetch(`${API_BASE}/api/admin/send-cycle-followup`, {
          method: "POST",
          headers: authHeader,
          body: JSON.stringify({ city: "NYC", preview: true }),
        }).then((r) => r.json()),
        fetch(`${API_BASE}/api/admin/send-cycle-followup`, {
          method: "POST",
          headers: authHeader,
          body: JSON.stringify({ city: "LA", preview: true }),
        }).then((r) => r.json()),
      ]);

      setMessageDrafts({
        nycAvailPoll: nycAvailPoll?.preview || "",
        laAvailPoll: laAvailPoll?.preview || "",
        nycReminderPoll: nycReminderPoll?.preview || "",
        laReminderPoll: laReminderPoll?.preview || "",
      });
    } catch (e) {
      showToast(`Failed to load previews: ${e.message}`, true);
    } finally {
      setLoadingPreviews(false);
    }
  }, []);

  const fetchOpenCycle = useCallback(async (tok) => {
    try {
      const res = await fetch(`${API_BASE}/api/admin/open-cycle`, {
        headers: { Authorization: `Basic ${tok}` },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to load open cycle");
      setOpenCycles(data.cycles ?? []);
    } catch (e) {
      showToast(e.message, true);
    }
  }, []);

  async function handleLogin(e) {
    e.preventDefault();
    setAuthError("");
    setAuthLoading(true);
    try {
      const basicToken = btoa(`${username}:${password}`);
    const res = await fetch(`${API_BASE}/api/admin/people`, {
        headers: { Authorization: `Basic ${basicToken}` },
      });
      if (res.status === 401) {
        setAuthError("Invalid credentials.");
        return;
      }
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Login failed");
      setToken(basicToken);
      setPeople(data.people ?? []);
      setAuthed(true);
      fetchCycleAvailability(basicToken);
      fetchOpenCycle(basicToken);
      fetchPreviews(basicToken);
    } catch (e) {
      setAuthError(e.message);
    } finally {
      setAuthLoading(false);
    }
  }

  function toggleSelect(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  function toggleAll() {
    const eligible = (filterCity === "all" ? people : people.filter((p) => p.city === filterCity))
      .filter((p) => ELIGIBLE_STATUSES.has(p.status))
      .map((p) => p.id);
    if (eligible.every((id) => selected.has(id))) {
      setSelected(new Set());
    } else {
      setSelected(new Set(eligible));
    }
  }

  async function handleAccept(person) {
    setAccepting((prev) => new Set(prev).add(person.id));
    try {
      const res = await fetch(`${API_BASE}/api/admin/accept`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Basic ${token}` },
        body: JSON.stringify({ personId: person.id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to send");
      if (data.result?.skipped) {
        showToast(`Already sent to ${person.first_name}`, true);
      } else {
        showToast(`Acceptance email queued for ${person.first_name} ${person.last_name}`);
        setPeople((prev) =>
          prev.map((p) => (p.id === person.id ? { ...p, acceptance_sent: true } : p))
        );
      }
    } catch (e) {
      showToast(e.message, true);
    } finally {
      setAccepting((prev) => {
        const next = new Set(prev);
        next.delete(person.id);
        return next;
      });
    }
  }

  async function handleMarkSent(person) {
    setMarking((prev) => new Set(prev).add(person.id));
    try {
      const res = await fetch(`${API_BASE}/api/admin/mark-sent`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Basic ${token}` },
        body: JSON.stringify({ personId: person.id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to mark");
      showToast(`Marked as sent for ${person.first_name} ${person.last_name}`);
      setPeople((prev) =>
        prev.map((p) => (p.id === person.id ? { ...p, acceptance_sent: true } : p))
      );
    } catch (e) {
      showToast(e.message, true);
    } finally {
      setMarking((prev) => {
        const next = new Set(prev);
        next.delete(person.id);
        return next;
      });
    }
  }

  async function handleSendAvailabilityCheck() {
    if (!window.confirm("Send availability check text to all eligible LA members?")) return;
    setSendingAvailCheck(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/send-availability-check`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Basic ${token}` },
        body: JSON.stringify({ message: messageDrafts.laAvailPoll }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to send");
      const summary = `Sent ${data.sent} text${data.sent !== 1 ? "s" : ""}${data.failed > 0 ? `, ${data.failed} failed` : ""}`;
      showToast(data.message || summary, data.failed > 0);
    } catch (e) {
      showToast(e.message, true);
    } finally {
      setSendingAvailCheck(false);
    }
  }

  async function handleSendAvailabilityCheckTest() {
    setSendingAvailCheckTest(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/send-availability-check`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Basic ${token}` },
        body: JSON.stringify({ test: true, message: messageDrafts.laAvailPoll }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to send");
      showToast(`Test sent to ${data.to}`);
    } catch (e) {
      showToast(e.message, true);
    } finally {
      setSendingAvailCheckTest(false);
    }
  }

  async function handleSendGroupText() {
    setSendingGroupText(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/send-group-text`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Basic ${token}` },
        body: JSON.stringify({ personIds: Array.from(selected), message: groupMessage }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to send");
      const summary = `Sent ${data.sent} text${data.sent !== 1 ? "s" : ""}${data.failed > 0 ? `, ${data.failed} failed` : ""}${data.groupId ? ` · Group ${data.groupId.slice(0, 8)}` : ""}`;
      showToast(summary, data.failed > 0);
      setShowGroupModal(false);
      setGroupMessage("");
      setSelected(new Set());
      if (data.groupId) {
        setPeople((prev) =>
          prev.map((p) => selected.has(p.id) ? { ...p, group_id: data.groupId } : p)
        );
      }
    } catch (e) {
      showToast(e.message, true);
    } finally {
      setSendingGroupText(false);
    }
  }

  async function handleSendNycAvailPoll() {
    if (!window.confirm("Send NYC availability poll text to all eligible NYC members?")) return;
    setSendingNycAvailPoll(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/send-nyc-availability-poll`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Basic ${token}` },
        body: JSON.stringify({ message: messageDrafts.nycAvailPoll }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to send");
      const summary = `Sent ${data.sent} text${data.sent !== 1 ? "s" : ""}${data.failed > 0 ? `, ${data.failed} failed` : ""}`;
      showToast(data.message || summary, data.failed > 0);
    } catch (e) {
      showToast(e.message, true);
    } finally {
      setSendingNycAvailPoll(false);
    }
  }

  async function handleSendNycAvailPollTest() {
    setSendingNycAvailPollTest(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/send-nyc-availability-poll`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Basic ${token}` },
        body: JSON.stringify({ test: true, message: messageDrafts.nycAvailPoll }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to send");
      showToast(`Test sent to ${data.to}`);
    } catch (e) {
      showToast(e.message, true);
    } finally {
      setSendingNycAvailPollTest(false);
    }
  }

  async function handleSendNycCycleFollowup() {
    if (!window.confirm("Send NYC cycle follow-up text to all eligible NYC members who haven't responded?")) return;
    setSendingNycCycleFollowup(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/send-cycle-followup`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Basic ${token}` },
        body: JSON.stringify({ city: "NYC", message: messageDrafts.nycReminderPoll }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to send");
      const summary = `Sent ${data.sent} text${data.sent !== 1 ? "s" : ""} (${data.skipped} already responded)${data.failed > 0 ? `, ${data.failed} failed` : ""}`;
      showToast(data.message || summary, data.failed > 0);
    } catch (e) {
      showToast(e.message, true);
    } finally {
      setSendingNycCycleFollowup(false);
    }
  }

  async function handleSendNycCycleFollowupTest() {
    if (!window.confirm("Send NYC reminder to +15104493714 (TEST ONLY)?")) return;
    setSendingNycCycleFollowupTest(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/send-cycle-followup`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Basic ${token}` },
        body: JSON.stringify({ city: "NYC", test: true, message: messageDrafts.nycReminderPoll }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to send");
      showToast(`Test sent to ${data.to} (would send to ${data.wouldSendTo} member${data.wouldSendTo !== 1 ? "s" : ""})`);
    } catch (e) {
      showToast(e.message, true);
    } finally {
      setSendingNycCycleFollowupTest(false);
    }
  }

  async function handleSendLaCycleFollowup() {
    if (!window.confirm("Send LA cycle follow-up text to all eligible LA members who haven't responded?")) return;
    setSendingLaCycleFollowup(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/send-cycle-followup`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Basic ${token}` },
        body: JSON.stringify({ city: "LA", message: messageDrafts.laReminderPoll }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to send");
      const summary = `Sent ${data.sent} text${data.sent !== 1 ? "s" : ""} (${data.skipped} already responded)${data.failed > 0 ? `, ${data.failed} failed` : ""}`;
      showToast(data.message || summary, data.failed > 0);
    } catch (e) {
      showToast(e.message, true);
    } finally {
      setSendingLaCycleFollowup(false);
    }
  }

  async function handleSendLaCycleFollowupTest() {
    setSendingLaCycleFollowupTest(true);
    try {
      const res = await fetch(`${API_BASE}/api/admin/send-cycle-followup`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Basic ${token}` },
        body: JSON.stringify({ city: "LA", test: true, message: messageDrafts.laReminderPoll }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to send");
      showToast(`Test sent to ${data.to} (would send to ${data.wouldSendTo} member${data.wouldSendTo !== 1 ? "s" : ""})`);
    } catch (e) {
      showToast(e.message, true);
    } finally {
      setSendingLaCycleFollowupTest(false);
    }
  }

  async function handleShoot(person) {
    setShootSending(person);
    try {
      const res = await fetch(`${API_BASE}/api/admin/shoot`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Basic ${token}` },
        body: JSON.stringify({ person }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed to send");
      showToast(`Sent to ${data.sent}`);
    } catch (e) {
      showToast(e.message, true);
    } finally {
      setShootSending(null);
    }
  }

  if (!authed) {
    return (
      <div className="admin-page admin-login">
        <div className="admin-login-card">
          <img src={LOGO} alt="Fika" className="admin-login-logo" />
          <h2>Admin Portal</h2>
          <form onSubmit={handleLogin}>
            <input
              type="text"
              placeholder="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              required
            />
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
            <button className="admin-login-btn" type="submit" disabled={authLoading}>
              {authLoading ? "Signing in…" : "Sign in"}
            </button>
          </form>
          {authError && <p className="admin-login-error">{authError}</p>}
        </div>
      </div>
    );
  }

  function toggleSort(field) {
    if (sortField === field) {
      setSortDir((d) => (d === "desc" ? "asc" : "desc"));
    } else {
      setSortField(field);
      setSortDir("desc");
    }
  }

  const cities = ["all", ...Array.from(new Set(people.map((p) => p.city).filter(Boolean))).sort()];

  let visiblePeople = filterCity === "all" ? people : people.filter((p) => p.city === filterCity);

  // Build match groups among cycle_available people based on specialty ↔ collaborator overlap
  const cycleAvailPeople = visiblePeople.filter((p) => p.cycle_available);
  const nonAvailPeople = visiblePeople.filter((p) => !p.cycle_available);

  const groups = [];
  const assignedToGroup = new Set();

  for (const person of cycleAvailPeople) {
    if (assignedToGroup.has(person.id)) continue;

    const group = [person];
    assignedToGroup.add(person.id);

    const personSpecs = new Set(
      (person.creative_specialties || "").split(",").map((s) => s.trim().toLowerCase()).filter(Boolean)
    );
    const personWants = new Set((person.collaborators || []).map((s) => s.trim().toLowerCase()));

    for (const other of cycleAvailPeople) {
      if (assignedToGroup.has(other.id) || group.length >= 3) continue;

      const otherSpecs = new Set(
        (other.creative_specialties || "").split(",").map((s) => s.trim().toLowerCase()).filter(Boolean)
      );
      const otherWants = new Set((other.collaborators || []).map((s) => s.trim().toLowerCase()));

      const personWantsOther = [...personWants].some((w) => otherSpecs.has(w));
      const otherWantsPerson = [...otherWants].some((w) => personSpecs.has(w));

      if (personWantsOther || otherWantsPerson) {
        group.push(other);
        assignedToGroup.add(other.id);
      }
    }

    groups.push(group);
  }

  const groupIndexMap = new Map();
  const groupPositionMap = new Map();
  groups.forEach((group, idx) => {
    group.forEach((p, i) => {
      groupIndexMap.set(p.id, idx);
      groupPositionMap.set(
        p.id,
        group.length === 1 ? "only" : i === 0 ? "first" : i === group.length - 1 ? "last" : "middle"
      );
    });
  });

  // Sort cycle_available people by group so members are adjacent, then non-available
  const sortedAvail = [...cycleAvailPeople].sort((a, b) => {
    const ai = groupIndexMap.get(a.id) ?? 9999;
    const bi = groupIndexMap.get(b.id) ?? 9999;
    if (ai !== bi) return ai - bi;
    if (sortField) {
      const av = a[sortField] ? 1 : 0;
      const bv = b[sortField] ? 1 : 0;
      return sortDir === "desc" ? bv - av : av - bv;
    }
    return 0;
  });

  const sortedNonAvail = sortField
    ? [...nonAvailPeople].sort((a, b) => {
        const av = a[sortField] ? 1 : 0;
        const bv = b[sortField] ? 1 : 0;
        return sortDir === "desc" ? bv - av : av - bv;
      })
    : nonAvailPeople;

  visiblePeople = [...sortedAvail, ...sortedNonAvail];

  const eligibleCount = visiblePeople.filter((p) => ELIGIBLE_STATUSES.has(p.status)).length;

  return (
    <div className="admin-page">
      <header className="admin-header">
        <img src={LOGO} alt="Fika" className="admin-header-logo" />
      </header>

      <div className="admin-open-cycle-banner">
        <span className="admin-open-cycle-label">Open Cycle</span>
        {openCycles.length === 0 ? (
          <span className="admin-open-cycle-value admin-open-cycle-value--empty">No cycle currently open</span>
        ) : (
          openCycles.map((cycle) => (
            <span key={cycle.id} className="admin-open-cycle-value">
              {(() => {
                try {
                  return new Date(cycle.cycle_date + "T00:00:00").toLocaleDateString("en-US", {
                    month: "long",
                    day: "numeric",
                    year: "numeric",
                  });
                } catch {
                  return cycle.cycle_date;
                }
              })()}
            </span>
          ))
        )}
      </div>

      <div className="admin-message-toolbar">
        <div className="admin-message-card">
          <h3 className="admin-message-title">NYC Avail Poll</h3>
          <label className="admin-message-label">Contents:</label>
          <textarea
            className="admin-message-textarea"
            rows={5}
            value={messageDrafts.nycAvailPoll}
            placeholder={loadingPreviews ? "Loading preview…" : ""}
            disabled={loadingPreviews}
            onChange={(e) => setMessageDrafts((prev) => ({ ...prev, nycAvailPoll: e.target.value }))}
          />
          <div className="admin-message-actions">
            <button
              className="admin-assign-btn"
              disabled={sendingNycAvailPoll || loadingPreviews}
              onClick={handleSendNycAvailPoll}
            >
              {sendingNycAvailPoll ? "Sending…" : "NYC Avail Poll"}
            </button>
            <button
              className="admin-tertiary-btn"
              disabled={sendingNycAvailPollTest || loadingPreviews}
              onClick={handleSendNycAvailPollTest}
            >
              {sendingNycAvailPollTest ? "Sending…" : "Test → 5104493714"}
            </button>
          </div>
        </div>

        <div className="admin-message-card">
          <h3 className="admin-message-title">NYC Reminder Poll</h3>
          <label className="admin-message-label">Contents:</label>
          <textarea
            className="admin-message-textarea"
            rows={5}
            value={messageDrafts.nycReminderPoll}
            placeholder={loadingPreviews ? "Loading preview…" : ""}
            disabled={loadingPreviews}
            onChange={(e) => setMessageDrafts((prev) => ({ ...prev, nycReminderPoll: e.target.value }))}
          />
          <div className="admin-message-actions">
            <button
              className="admin-assign-btn"
              disabled={sendingNycCycleFollowup || loadingPreviews}
              onClick={handleSendNycCycleFollowup}
            >
              {sendingNycCycleFollowup ? "Sending…" : "NYC Remind Poll"}
            </button>
            <button
              className="admin-tertiary-btn"
              disabled={sendingNycCycleFollowupTest || loadingPreviews}
              onClick={handleSendNycCycleFollowupTest}
            >
              {sendingNycCycleFollowupTest ? "Sending…" : "Test → 5104493714"}
            </button>
          </div>
        </div>

        <div className="admin-message-card">
          <h3 className="admin-message-title">LA Avail Poll</h3>
          <label className="admin-message-label">Contents:</label>
          <textarea
            className="admin-message-textarea"
            rows={5}
            value={messageDrafts.laAvailPoll}
            placeholder={loadingPreviews ? "Loading preview…" : ""}
            disabled={loadingPreviews}
            onChange={(e) => setMessageDrafts((prev) => ({ ...prev, laAvailPoll: e.target.value }))}
          />
          <div className="admin-message-actions">
            <button
              className="admin-assign-btn"
              disabled={sendingAvailCheck || loadingPreviews}
              onClick={handleSendAvailabilityCheck}
            >
              {sendingAvailCheck ? "Sending…" : "LA Avail Poll"}
            </button>
            <button
              className="admin-tertiary-btn"
              disabled={sendingAvailCheckTest || loadingPreviews}
              onClick={handleSendAvailabilityCheckTest}
            >
              {sendingAvailCheckTest ? "Sending…" : "Test → 5104493714"}
            </button>
          </div>
        </div>

        <div className="admin-message-card">
          <h3 className="admin-message-title">LA Reminder Poll</h3>
          <label className="admin-message-label">Contents:</label>
          <textarea
            className="admin-message-textarea"
            rows={5}
            value={messageDrafts.laReminderPoll}
            placeholder={loadingPreviews ? "Loading preview…" : ""}
            disabled={loadingPreviews}
            onChange={(e) => setMessageDrafts((prev) => ({ ...prev, laReminderPoll: e.target.value }))}
          />
          <div className="admin-message-actions">
            <button
              className="admin-assign-btn"
              disabled={sendingLaCycleFollowup || loadingPreviews}
              onClick={handleSendLaCycleFollowup}
            >
              {sendingLaCycleFollowup ? "Sending…" : "LA Remind Poll"}
            </button>
            <button
              className="admin-tertiary-btn"
              disabled={sendingLaCycleFollowupTest || loadingPreviews}
              onClick={handleSendLaCycleFollowupTest}
            >
              {sendingLaCycleFollowupTest ? "Sending…" : "Test → 5104493714"}
            </button>
          </div>
        </div>
      </div>

      <div className="admin-toolbar">
        <div className="admin-city-filters">
          {cities.map((c) => (
            <button
              key={c}
              className={`admin-city-btn${filterCity === c ? " admin-city-btn--active" : ""}`}
              onClick={() => setFilterCity(c)}
            >
              {c === "all" ? "All" : c}
            </button>
          ))}
        </div>
        {selected.size > 0 && (
          <button
            className="admin-assign-btn admin-group-msg-btn"
            onClick={() => setShowGroupModal(true)}
          >
            Send Group Message ({selected.size})
          </button>
        )}
        <span className="admin-user-count">{visiblePeople.length} / {people.length} users</span>
        <button className="admin-refresh-btn" onClick={() => fetchPeople(token)}>
          Refresh
        </button>
      </div>

      <div className="admin-shoot-section">
        <span className="admin-shoot-label">Shoot</span>
        {["brett", "darcy", "kristen", "sonika"].map((person) => (
          <button
            key={person}
            className="admin-shoot-pill"
            disabled={shootSending === person}
            onClick={() => handleShoot(person)}
          >
            {shootSending === person ? "Sending…" : person.charAt(0).toUpperCase() + person.slice(1)}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="admin-loading">Loading members…</div>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    onChange={toggleAll}
                    checked={
                      eligibleCount > 0 &&
                      people
                        .filter((p) => ELIGIBLE_STATUSES.has(p.status))
                        .every((p) => selected.has(p.id))
                    }
                  />
                </th>
                <th>Name</th>
                <th>Email</th>
                <th>Creative Specialties</th>
                <th>Current Project</th>
                <th>Progress Timeline</th>
                <th>Portfolio / LinkedIn / Instagram</th>
                <th>Status</th>
                <th>Accept User</th>
                <th className="admin-th-sortable" onClick={() => toggleSort("profile_created")}>
                  Profile Created {sortField === "profile_created" ? (sortDir === "desc" ? "↓" : "↑") : "↕"}
                </th>
                <th className="admin-th-sortable" onClick={() => toggleSort("avail_uploaded")}>
                  Avail Uploaded {sortField === "avail_uploaded" ? (sortDir === "desc" ? "↓" : "↑") : "↕"}
                </th>
                <th>Group ID</th>
              </tr>
            </thead>
            <tbody>
              {visiblePeople.map((person) => {
                const isEligible = ELIGIBLE_STATUSES.has(person.status);
                const isSelected = selected.has(person.id);
                const isAccepting = accepting.has(person.id);
                const sub = person.subscription_status || "";
                const isActiveSub = ["free", "trialing", "active"].includes(sub);
                const isExpiredSub = ["canceled", "incomplete_expired", "unpaid", "past_due"].includes(sub);
                const groupIdx = groupIndexMap.get(person.id);
                const groupPos = groupPositionMap.get(person.id);
                const groupColorClass = groupIdx !== undefined
                  ? (groupIdx % 2 === 0 ? "admin-group-red" : "admin-group-green")
                  : "";
                const groupPosClass = groupPos ? `admin-group-${groupPos}` : "";

                const rowClass = [
                  isSelected
                    ? "admin-row-selected"
                    : person.cycle_available
                    ? "admin-row-cycle-available"
                    : person.group_id
                    ? "admin-row-red"
                    : person.status === "PAYMENT_COMPLETE"
                    ? "admin-row-dark-green"
                    : isActiveSub
                    ? "admin-row-green"
                    : isExpiredSub
                    ? "admin-row-red"
                    : "",
                  groupColorClass,
                  groupPosClass,
                ].filter(Boolean).join(" ");

                return (
                  <tr key={person.id} className={`${rowClass} admin-row-clickable`} onClick={() => setSelectedPerson(person)}>
                    <td onClick={(e) => e.stopPropagation()}>
                      {isEligible && (
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelect(person.id)}
                        />
                      )}
                    </td>

                    <td className="admin-name">
                      {person.first_name} {person.last_name}
                    </td>

                    <td className="admin-cell-truncate">{person.email || "—"}</td>

                    <td
                      className={expandedSpecialties.has(person.id) ? "admin-cell-expand" : "admin-cell-truncate admin-cell-expandable"}
                      onClick={() => person.creative_specialties && toggleExpand(expandedSpecialties, setExpandedSpecialties, person.id)}
                      title={expandedSpecialties.has(person.id) ? "Click to collapse" : person.creative_specialties || ""}
                    >
                      {person.creative_specialties || "—"}
                    </td>

                    <td
                      className={expandedProject.has(person.id) ? "admin-cell-expand" : "admin-cell-truncate admin-cell-expandable"}
                      onClick={() => person.current_project && toggleExpand(expandedProject, setExpandedProject, person.id)}
                      title={expandedProject.has(person.id) ? "Click to collapse" : person.current_project || ""}
                    >
                      {person.current_project || "—"}
                    </td>

                    <td style={{ whiteSpace: "nowrap" }}>
                      {person.progress_timeline || "—"}
                    </td>

                    <td>
                      <div className="admin-links">
                        {person.portfolio && (
                          <a
                            className="admin-link"
                            href={person.portfolio}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Portfolio
                          </a>
                        )}
                        {person.linkedin_handle && (
                          <a
                            className="admin-link"
                            href={linkedInUrl(person.linkedin_handle)}
                            target="_blank"
                            rel="noreferrer"
                          >
                            LinkedIn
                          </a>
                        )}
                        {person.instagram_handle && (
                          <a
                            className="admin-link"
                            href={instagramUrl(person.instagram_handle)}
                            target="_blank"
                            rel="noreferrer"
                          >
                            Instagram
                          </a>
                        )}
                        {!person.portfolio && !person.linkedin_handle && !person.instagram_handle && "—"}
                      </div>
                    </td>

                    <td>
                      <span className={isEligible ? "admin-status-ok" : "admin-status-other"}>
                        {person.status || "—"}
                      </span>
                    </td>

                    <td onClick={(e) => e.stopPropagation()}>
                      {isEligible ? (
                        <div style={{ display: "flex", gap: "6px", alignItems: "center", flexWrap: "wrap" }}>
                          {person.acceptance_sent && (
                            <span className="admin-accept-sent">Sent ✓</span>
                          )}
                          <button
                            className="admin-accept-btn"
                            onClick={() => handleAccept(person)}
                            disabled={isAccepting}
                          >
                            {isAccepting ? "Sending…" : "Accept"}
                          </button>
                          <button
                            className="admin-mark-sent-btn"
                            onClick={() => handleMarkSent(person)}
                            disabled={marking.has(person.id)}
                          >
                            {marking.has(person.id) ? "…" : "Mark Sent"}
                          </button>
                        </div>
                      ) : (
                        "—"
                      )}
                    </td>

                    <td>{person.profile_created ? <Check /> : <X />}</td>
                    <td>{person.avail_uploaded ? <Check /> : <X />}</td>

                    <td>
                      {person.group_id ? (
                        <span className="admin-group-id" title={person.group_id}>
                          {person.group_id}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {cycleAvailData !== null && (
        <div className="admin-cycles-section">
          <div className="admin-cycles-header">
            <h2 className="admin-cycles-title">Cycle Availability</h2>
            <button
              className="admin-refresh-btn"
              onClick={() => fetchCycleAvailability(token)}
              disabled={loadingCycleAvail}
            >
              {loadingCycleAvail ? "Loading…" : "Refresh"}
            </button>
          </div>

          {loadingCycleAvail ? (
            <div className="admin-loading" style={{ minHeight: 80 }}>Loading cycle data…</div>
          ) : Object.keys(cycleAvailData).length === 0 ? (
            <p className="admin-cycles-empty">No match cycles found.</p>
          ) : (
            Object.entries(cycleAvailData).map(([city, cycles]) => (
              <div key={city} className="admin-city-cycles">
                {cycles.map((cycle) => {
                  const dateLabel = (() => {
                    try {
                      return new Date(cycle.cycle_date + "T00:00:00").toLocaleDateString("en-US", {
                        month: "long",
                        day: "numeric",
                      });
                    } catch {
                      return cycle.cycle_date;
                    }
                  })();

                  return (
                    <div key={cycle.cycle_id} className="admin-cycle-block">
                      <h3 className="admin-cycle-heading">
                        {dateLabel}
                        <span className="admin-cycle-heading-city">{city}</span>
                      </h3>
                      <div className="admin-cycle-options">
                        {[1, 2, 3].map((opt) => (
                          <div key={opt} className="admin-cycle-card">
                            <div className="admin-cycle-card-label">{opt}.</div>
                            {(cycle.options[opt] || []).length === 0 ? (
                              <div className="admin-cycle-empty">—</div>
                            ) : (
                              (cycle.options[opt] || []).map((p) => (
                                <div key={p.person_id} className="admin-cycle-person">
                                  <span className="admin-cycle-person-name">{p.name}</span>
                                  {p.phone && (
                                    <span className="admin-cycle-person-phone"> - {p.phone}</span>
                                  )}
                                  {p.zip_code && (
                                    <span className="admin-cycle-person-zip"> - {p.zip_code}</span>
                                  )}
                                </div>
                              ))
                            )}
                          </div>
                        ))}
                        <div className="admin-cycle-card admin-cycle-card--cant">
                          <div className="admin-cycle-card-label">4. can't make it</div>
                          {(cycle.options[4] || []).length === 0 ? (
                            <div className="admin-cycle-empty">—</div>
                          ) : (
                            (cycle.options[4] || []).map((p) => (
                              <div key={p.person_id} className="admin-cycle-person">
                                <span className="admin-cycle-person-name">{p.name}</span>
                                {p.phone && (
                                  <span className="admin-cycle-person-phone"> - {p.phone}</span>
                                )}
                                {p.zip_code && (
                                  <span className="admin-cycle-person-zip"> - {p.zip_code}</span>
                                )}
                                {p.no_response && (
                                  <span className="admin-cycle-no-response"> (no response)</span>
                                )}
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))
          )}
        </div>
      )}

      {selectedPerson && (
        <>
          <div className="admin-pane-overlay" onClick={() => setSelectedPerson(null)} />
          <div className="admin-pane">
            <button className="admin-pane-close" onClick={() => setSelectedPerson(null)}>✕</button>

            <span className={`admin-pane-status ${ELIGIBLE_STATUSES.has(selectedPerson.status) ? "admin-pane-status--ok" : "admin-pane-status--other"}`}>
              {selectedPerson.status || "—"}
            </span>

            <h2 className="admin-pane-name">{selectedPerson.first_name} {selectedPerson.last_name}</h2>

            {selectedPerson.creative_specialties && (
              <p className="admin-pane-specialties">{selectedPerson.creative_specialties}</p>
            )}

            {selectedPerson.bio && (
              <p className="admin-pane-bio">{selectedPerson.bio}</p>
            )}

            {selectedPerson.current_project && (
              <div className="admin-pane-section">
                <span className="admin-pane-label">Current project</span>
                <p className="admin-pane-value">{selectedPerson.current_project}</p>
              </div>
            )}

            {selectedPerson.progress_timeline && (
              <div className="admin-pane-section">
                <span className="admin-pane-label">Timeline</span>
                <p className="admin-pane-value">{selectedPerson.progress_timeline}</p>
              </div>
            )}

            {selectedPerson.collaborators?.length > 0 && (
              <div className="admin-pane-section">
                <span className="admin-pane-label">Looking to collaborate with</span>
                <p className="admin-pane-value">{selectedPerson.collaborators.join(", ")}</p>
              </div>
            )}

            {selectedPerson.current_role_and_company && (
              <div className="admin-pane-section">
                <span className="admin-pane-label">Role</span>
                <p className="admin-pane-value">{selectedPerson.current_role_and_company}</p>
              </div>
            )}

            {selectedPerson.years_in_creative_industry && (
              <div className="admin-pane-section">
                <span className="admin-pane-label">Years in industry</span>
                <p className="admin-pane-value">{selectedPerson.years_in_creative_industry}</p>
              </div>
            )}

            {(selectedPerson.portfolio || selectedPerson.linkedin_handle || selectedPerson.instagram_handle) && (
              <div className="admin-pane-links">
                {selectedPerson.portfolio && (
                  <a className="admin-link" href={selectedPerson.portfolio} target="_blank" rel="noreferrer">Portfolio</a>
                )}
                {selectedPerson.linkedin_handle && (
                  <a className="admin-link" href={linkedInUrl(selectedPerson.linkedin_handle)} target="_blank" rel="noreferrer">LinkedIn</a>
                )}
                {selectedPerson.instagram_handle && (
                  <a className="admin-link" href={instagramUrl(selectedPerson.instagram_handle)} target="_blank" rel="noreferrer">Instagram</a>
                )}
              </div>
            )}

            <div className="admin-pane-checks">
              <div className="admin-pane-check-row">
                {selectedPerson.acceptance_sent ? <Check /> : <X />}
                <span>Acceptance sent</span>
              </div>
              <div className="admin-pane-check-row">
                {selectedPerson.profile_created ? <Check /> : <X />}
                <span>Profile created</span>
              </div>
              <div className="admin-pane-check-row">
                {selectedPerson.avail_uploaded ? <Check /> : <X />}
                <span>Availability submitted</span>
              </div>
            </div>

            {selectedPerson.cycle_available && (
              <div className="admin-pane-section">
                <span className="admin-pane-label">Typical availability</span>
                <pre className="admin-pane-avail-raw">
                  {selectedPerson.typical_availability != null
                    ? JSON.stringify(selectedPerson.typical_availability, null, 2)
                    : "—"}
                </pre>
              </div>
            )}

            {selectedPerson.group_id && (
              <div className="admin-pane-section">
                <span className="admin-pane-label">Group ID</span>
                <p className="admin-pane-value admin-pane-mono">{selectedPerson.group_id}</p>
              </div>
            )}
          </div>
        </>
      )}

      {showGroupModal && (
        <>
          <div className="admin-modal-overlay" onClick={() => setShowGroupModal(false)} />
          <div className="admin-modal">
            <div className="admin-modal-header">
              <h3 className="admin-modal-title">Send Group Message</h3>
              <button className="admin-pane-close" onClick={() => setShowGroupModal(false)}>✕</button>
            </div>

            <p className="admin-modal-subtitle">
              {selected.size} recipient{selected.size !== 1 ? "s" : ""} · will be added to a new match group
            </p>

            <div className="admin-modal-recipients">
              {people
                .filter((p) => selected.has(p.id))
                .map((p) => (
                  <div key={p.id} className="admin-modal-recipient">
                    <span className="admin-modal-recipient-name">{p.first_name} {p.last_name}</span>
                    <span className="admin-modal-recipient-phone">{p.phone || "no phone"}</span>
                  </div>
                ))}
            </div>

            <textarea
              className="admin-modal-textarea"
              placeholder="Type your message…"
              value={groupMessage}
              onChange={(e) => setGroupMessage(e.target.value)}
              rows={5}
            />

            <div className="admin-modal-actions">
              <button className="admin-tertiary-btn" onClick={() => setShowGroupModal(false)}>
                Cancel
              </button>
              <button
                className="admin-assign-btn"
                disabled={sendingGroupText || !groupMessage.trim()}
                onClick={handleSendGroupText}
              >
                {sendingGroupText ? "Sending…" : `Send to ${selected.size}`}
              </button>
            </div>
          </div>
        </>
      )}

      {toast && (
        <Toast
          key={toast.key}
          message={toast.message}
          isError={toast.isError}
          onDone={() => setToast(null)}
        />
      )}
    </div>
  );
}
